<template>
  <div class="bbr-canonical-exercises--similar">
    <main-app-bar>
      <template v-slot:title> Similar Canonical Exercises </template>
    </main-app-bar>

    <div class="mt-5 px-12 mb-10">
      <p class="grey--text text--darken-1">
        Pairs of canonical exercises with near-identical names. Merge a pair to
        move all exercises onto the kept canonical exercise and delete the other
        — weight history is preserved.
      </p>

      <div v-if="isLoading" class="text-center py-12">
        <v-progress-circular
          indeterminate
          color="primary"
          size="48"
          width="4"
        />
        <div class="mt-4 grey--text text--darken-1">
          Finding similar canonical exercises… this can take a few seconds.
        </div>
      </div>

      <v-simple-table v-else>
        <thead>
          <tr>
            <th>Canonical Exercise</th>
            <th class="col-count">Linked</th>
            <th>Similar To</th>
            <th class="col-count">Linked</th>
            <th class="col-similarity text-right">Similarity</th>
            <th class="col-merge text-center">Merge</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(pair, index) in pairs" :key="index">
            <td>
              <router-link :to="ceLink(pair.a.id)">
                {{ pair.a.name }}
              </router-link>
            </td>
            <td class="col-count">{{ pair.a.exercises_count }}</td>
            <td>
              <router-link :to="ceLink(pair.b.id)">
                {{ pair.b.name }}
              </router-link>
            </td>
            <td class="col-count">{{ pair.b.exercises_count }}</td>
            <td class="col-similarity text-right">{{ pair.similarity }}%</td>
            <td class="col-merge text-center">
              <v-btn
                small
                outlined
                color="primary"
                class="mr-2"
                @click="confirmMerge(pair, 'a')"
              >
                Keep A
              </v-btn>
              <v-btn
                small
                outlined
                color="primary"
                @click="confirmMerge(pair, 'b')"
              >
                Keep B
              </v-btn>
            </td>
          </tr>
        </tbody>
      </v-simple-table>

      <no-list
        v-if="!isLoading && !pairs.length"
        details="No similar canonical exercises found"
      />
    </div>

    <v-dialog v-model="showMergeDialog" width="600" persistent>
      <v-card class="mx-auto" outlined>
        <v-card-title class="headline">Merge Canonical Exercises</v-card-title>

        <v-divider />

        <v-card-text v-if="merge.target" class="py-5">
          Move all exercises linked to "<strong>{{ merge.source.name }}</strong
          >" onto "<strong>{{ merge.target.name }}</strong
          >", then delete "<strong>{{ merge.source.name }}</strong
          >"? Weight history is preserved.
        </v-card-text>

        <v-divider />

        <v-card-actions class="px-6 pb-6 pt-5">
          <v-spacer />

          <v-btn
            class="px-12 error--text"
            text
            :disabled="mergeBusy"
            @click="showMergeDialog = false"
          >
            Cancel
          </v-btn>

          <v-btn
            class="primary px-12"
            text
            :loading="mergeBusy"
            :disabled="mergeBusy"
            @click="doMerge"
          >
            Merge
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import MainAppBar from '@/layouts/shared/MainAppBar'
import NoList from '@/components/elements/NoList'

import { mapActions } from 'vuex'

export default {
  name: 'SimilarCanonicalExercises',

  components: {
    MainAppBar,
    NoList,
  },

  data() {
    return {
      pairs: [],
      isLoading: false,
      showMergeDialog: false,
      mergeBusy: false,
      merge: {
        target: null,
        source: null,
      },
    }
  },

  mounted() {
    this.fetch()
  },

  methods: {
    ...mapActions({
      getSimilarCanonicalExercises:
        'canonicalExercises/getSimilarCanonicalExercises',
      mergeCanonicalExercises: 'canonicalExercises/mergeCanonicalExercises',
    }),

    async fetch() {
      this.isLoading = true
      try {
        this.pairs = await this.getSimilarCanonicalExercises({})
      } finally {
        this.isLoading = false
      }
    },

    ceLink(id) {
      return { name: 'canonical-exercise', params: { id } }
    },

    confirmMerge(pair, keep) {
      // Keep the chosen side as the target; the other side is merged in and deleted.
      this.merge = {
        target: keep === 'a' ? pair.a : pair.b,
        source: keep === 'a' ? pair.b : pair.a,
      }
      this.showMergeDialog = true
    },

    async doMerge() {
      this.mergeBusy = true
      try {
        await this.mergeCanonicalExercises({
          targetId: this.merge.target.id,
          sourceId: this.merge.source.id,
        })
        this.showMergeDialog = false
        await this.fetch()
      } finally {
        this.mergeBusy = false
      }
    },
  },
}
</script>

<style scoped>
.col-count {
  width: 90px;
}
.col-similarity {
  width: 120px;
}
.col-merge {
  width: 200px;
}
</style>
